import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Loader } from './Loader.jsx';

// A simple debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

// --- Sidebar Component ---
const MemberSidebar = ({ member, onClose }) => {
    if (!member) return null;
    return (
        <div className="fixed top-0 right-0 h-full w-full sm:w-96 md:w-[450px] bg-white shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-3xl leading-none z-10 p-1 hover:bg-gray-200 rounded-full" aria-label="Close">&times;</button>
            <div className="p-8 pt-16 overflow-y-auto h-full">
                <div className="flex flex-col items-center text-center">
                    <img src={member.profilePictureUrl ? `https://members.abssvss.com${member.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=8b0000&color=fff&size=128`} alt={`${member.firstName} ${member.lastName}`} className="w-32 h-32 rounded-full object-cover border-4 border-brand-accent mb-4" />
                    <h2 className="text-3xl font-bold text-brand-primary">{member.firstName} {member.lastName}</h2>
                    <p className="text-lg text-gray-600">{member.position || 'Member'}</p>
                    <p className="text-sm text-gray-500 mt-1">{member.city ? `${member.city}, ${member.state}` : member.state}</p>
                    <div className="border-t w-full my-6"></div>
                    <div className="text-left w-full"><h3 className="font-bold text-gray-800 mb-2">About</h3><p className="text-gray-600">{member.bio || 'No biography provided.'}</p></div>
                    <Link to={`/member/${member.memberId}`} className="mt-6 inline-block bg-brand-accent text-white font-bold py-2 px-6 rounded-full hover:bg-orange-500 transition-colors">View Full Profile</Link>
                </div>
            </div>
        </div>
    );
};

function MemberDirectory() {
    const [members, setMembers] = useState([]);
    const [nationalPresident, setNationalPresident] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isPaginating, setIsPaginating] = useState(false);
    const [error, setError] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ states: [], positions: [] });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterState, setFilterState] = useState('');
    const [filterPosition, setFilterPosition] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const isInitialMount = useRef(true);

    const fetchMembers = useCallback(async (currentPage, search, state, position) => {
        if (currentPage > 1) setIsPaginating(true);
        setError(null);
        
        try {
            const params = new URLSearchParams({ page: currentPage, limit: 26, search, state, position });
            const response = await fetch(`https://members.abssvss.com/members_api/get_all_members.php?${params.toString()}`);
            if (!response.ok) throw new Error('Failed to fetch members.');
            const data = await response.json();
            setMembers(data.members || []);
            setTotalPages(data.totalPages);
        } catch (e) {
            setError(e.message);
        } finally {
            if (currentPage > 1) setIsPaginating(false);
        }
    }, []);
    
    useEffect(() => {
        const fetchInitialData = async () => {
             try {
                // Fetch President and Filter Options in parallel
                const [presidentRes, optionsRes] = await Promise.all([
                    fetch(`https://members.abssvss.com/members_api/get_national_president.php`),
                    fetch(`https://members.abssvss.com/members_api/get_filter_options.php`)
                ]);
                const presidentData = await presidentRes.json();
                const optionsData = await optionsRes.json();
                setNationalPresident(presidentData);
                setFilterOptions(optionsData);
            } catch (error) {
                console.error("Could not fetch initial data:", error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const loadMembers = async () => {
            setIsInitialLoading(true);
            const fetchDataPromise = fetchMembers(1, debouncedSearchTerm, filterState, filterPosition);
            const minTimePromise = new Promise(resolve => setTimeout(resolve, 2000));
            await Promise.all([fetchDataPromise, minTimePromise]);
            setIsInitialLoading(false);
        };
        setPage(1);
        loadMembers();
    }, [debouncedSearchTerm, filterState, filterPosition, fetchMembers]);
    
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        fetchMembers(page, debouncedSearchTerm, filterState, filterPosition);
    }, [page]);
    
    const handleMemberClick = (member) => setSelectedMember(member);
    const handleCloseSidebar = () => setSelectedMember(null);
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const generateRows = (members, pattern) => {
        const rows = [];
        if (members.length > 0) {
            let i = 0;
            let rowNum = 0;
            while (i < members.length) {
                const count = pattern[rowNum % pattern.length];
                rows.push(members.slice(i, i + count));
                i += count;
                rowNum++;
            }
        }
        return rows;
    };
    
    const laptopRows = generateRows(members, [7, 6]);
    const tabletRows = generateRows(members, [5, 4]);
    const mobileRows = generateRows(members, [3, 2]);

    return (
        <>
            <style>{`
                .hexagon-shape {
                    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
                }
                .president-glow {
                    animation: pulse 2s infinite;
                }
                @keyframes pulse {
                    0%, 100% { box-shadow: 0 0 10px #FF9933, 0 0 15px #FF9933; }
                    50% { box-shadow: 0 0 25px #FF9933, 0 0 40px #FF9933; }
                }
                /* Fallback for older browsers that don't support clip-path */
                @supports not (clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)) {
                    .hexagon-shape {
                        clip-path: none;
                        border-radius: 9999px; /* Make it a circle */
                    }
                }
            `}</style>
            
            <AnimatePresence>{isInitialLoading && <Loader />}</AnimatePresence>
            
            <div className={`w-full py-16 px-4 overflow-x-hidden transition-opacity duration-500 ${isInitialLoading ? 'opacity-0' : 'opacity-100'}`}>
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    <h1 className="text-4xl font-bold text-brand-primary">Our Members</h1>
                    <p className="text-lg text-gray-600 mt-2">The dedicated members of our community.</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 p-4 bg-gray-50 rounded-lg border max-w-4xl mx-auto">
                    <input type="text" placeholder="Search by name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="p-3 bg-white rounded-md border" />
                    <select value={filterState} onChange={(e) => setFilterState(e.target.value)} className="p-3 bg-white rounded-md border">
                        <option value="">All States</option>
                        {filterOptions.states.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={filterPosition} onChange={(e) => setFilterPosition(e.target.value)} className="p-3 bg-white rounded-md border">
                        <option value="">All Positions</option>
                        {filterOptions.positions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>

                <div className={`transition-opacity duration-300 ${isPaginating ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                    {error ? ( <p className="text-center text-red-500">{error}</p> ) : (
                        <>
                            {/* National President Section */}
                            {nationalPresident && page === 1 && !searchTerm && !filterState && !filterPosition && (
                                <div className="mb-12 flex flex-col items-center">
                                    <h2 className="text-2xl font-bold text-brand-primary mb-4">National President</h2>
                                    <div className="cursor-pointer group p-1" onClick={() => handleMemberClick(nationalPresident)}>
                                        <div className="hexagon-shape w-48 h-52 bg-brand-accent p-1 president-glow">
                                            <img src={nationalPresident.profilePictureUrl ? `https://members.abssvss.com${nationalPresident.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${nationalPresident.firstName}+${nationalPresident.lastName}&background=8b0000&color=fff&size=128`} alt={`${nationalPresident.firstName} ${nationalPresident.lastName}`} className="hexagon-shape w-full h-full object-cover bg-gray-200" />
                                        </div>
                                    </div>
                                    <div className="border-t w-1/2 mx-auto mt-12"></div>
                                </div>
                            )}

                            {/* Honeycomb Grids */}
                            <div className="hidden lg:flex justify-center">
                                <div className="flex flex-col items-center">
                                    {laptopRows.map((rowItems, index) => (
                                       rowItems.length > 0 && (
                                            <div key={index} className={`flex ${index % 2 !== 0 ? '' : ''} ${index > 0 ? 'mt-[-2.5rem]' : ''}`}>
                                                {rowItems.map(member => (
                                                    <div key={member.memberId} className="cursor-pointer group p-1 mx-1" onClick={() => handleMemberClick(member)}>
                                                        <div className={`hexagon-shape w-36 h-40 bg-brand-accent p-0.5 ${member.position === 'National President' ? 'president-glow' : ''}`}>
                                                            <img src={member.profilePictureUrl ? `https://members.abssvss.com${member.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=8b0000&color=fff&size=128`} alt={`${member.firstName} ${member.lastName}`} className="hexagon-shape w-full h-full object-cover bg-gray-200 filter grayscale group-hover:grayscale-0 transition-all duration-300" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                       )
                                    ))}
                                </div>
                            </div>
    
                            <div className="hidden sm:flex lg:hidden justify-center">
                                <div className="flex flex-col items-center">
                                    {tabletRows.map((rowItems, index) => (
                                       rowItems.length > 0 && (
                                            <div key={index} className={`flex ${index % 2 !== 0 ? '' : ''} ${index > 0 ? 'mt-[-2.25rem]' : ''}`}>
                                                {rowItems.map(member => (
                                                    <div key={member.memberId} className="cursor-pointer group p-1 mx-1" onClick={() => handleMemberClick(member)}>
                                                        <div className={`hexagon-shape w-32 h-36 bg-brand-accent p-0.5 ${member.position === 'National President' ? 'president-glow' : ''}`}>
                                                            <img src={member.profilePictureUrl ? `https://members.abssvss.com${member.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=8b0000&color=fff&size=128`} alt={`${member.firstName} ${member.lastName}`} className="hexagon-shape w-full h-full object-cover bg-gray-200 filter grayscale group-hover:grayscale-0 transition-all duration-300" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                       )
                                    ))}
                                </div>
                            </div>
    
                            <div className="sm:hidden flex flex-col items-center">
                                 {mobileRows.map((rowItems, index) => (
                                   rowItems.length > 0 && (
                                        <div key={index} className={`flex justify-center ${index % 2 !== 0 ? 'ml-0' : ''} ${index > 0 ? 'mt-[-1.75rem]' : ''}`}>
                                            {rowItems.map(member => (
                                                <div key={member.memberId} className="cursor-pointer group p-1 mx-1" onClick={() => handleMemberClick(member)}>
                                                    <div className={`hexagon-shape w-24 h-28 bg-brand-accent p-0.5 ${member.position === 'National President' ? 'president-glow' : ''}`}>
                                                        <img src={member.profilePictureUrl ? `https://members.abssvss.com${member.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=8b0000&color=fff&size=128`} alt={`${member.firstName} ${member.lastName}`} className="hexagon-shape w-full h-full object-cover bg-gray-200 filter grayscale group-hover:grayscale-0 transition-all duration-300" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                   )
                                ))}
                            </div>
                        </>
                    )}
                </div>
                
                <div className="mt-16 flex justify-center items-center gap-4">
                    <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1 || isPaginating || isInitialLoading} className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">&larr; Previous</button>
                    <span className="text-gray-700">Page {page} of {totalPages}</span>
                    <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages || isPaginating || isInitialLoading} className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">Next &rarr;</button>
                </div>
                
                <div className={`fixed top-0 right-0 h-full w-full sm:w-96 md:w-[450px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-[100] ${selectedMember ? 'translate-x-0' : 'translate-x-full'}`}>
                    <MemberSidebar member={selectedMember} onClose={handleCloseSidebar} />
                </div>
                {selectedMember && <div onClick={handleCloseSidebar} className="fixed inset-0 bg-black bg-opacity-60 z-[99]"></div>}
            </div>
        </>
    );
}

export default MemberDirectory;

